import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { GOOGLE_CONFIG } from '../../app-config';
import { dynamicSheetManager } from '../../services/dynamic-sheet-manager';
import type { ModalState } from '../../types';
import type { SheetInfo } from '../../services/dynamic-sheet-manager';

interface TableFilterSidebarProps {
  modalState: ModalState;
  tableInfos: { [tableName: string]: any }; // Table metadata keyed by table name
  tableCounts: { [tableName: string]: number };
  onStateChange: (updates: Partial<ModalState>) => void;
}

const TableFilterSidebar: React.FC<TableFilterSidebarProps> = ({
  modalState,
  tableInfos,
  tableCounts,
  onStateChange
}) => {
  const tableNames = Object.keys(tableInfos);
  const allTablesSelected = tableNames.every(name => modalState.selectedTables.has(name));
  const noTablesSelected = modalState.selectedTables.size === 0;

  // State to track sheet information for building proper Google Sheets URLs
  const [sheetInfoMap, setSheetInfoMap] = useState<Map<string, SheetInfo>>(new Map());
  const [isLoadingSheetInfo, setIsLoadingSheetInfo] = useState(false);
  const [hoveredTable, setHoveredTable] = useState<string | null>(null);
  const [hoverTimeoutId, setHoverTimeoutId] = useState<number | null>(null);
  const [unconfiguredTables, setUnconfiguredTables] = useState<Set<string>>(new Set());
  const [accessErrorTables, setAccessErrorTables] = useState<Map<string, string>>(new Map());

  // Ref map to track icon positions for popover placement
  const iconRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Load sheet information for all tables on mount to check configuration
  useEffect(() => {
    const checkAllTablesConfiguration = async () => {
      const unconfigured = new Set<string>();
      const accessErrors = new Map<string, string>();

      for (const tableName of tableNames) {
        try {
          // Check if table has no configuration
          if (dynamicSheetManager.hasNoConfiguration(tableName)) {
            accessErrors.set(tableName, 'No spreadsheet configured');
            continue;
          }

          // Check if table has access errors from previous discovery
          if (dynamicSheetManager.hasAccessError(tableName)) {
            const error = dynamicSheetManager.getAccessError(tableName) || 'Access denied';
            accessErrors.set(tableName, error);
            continue;
          }

          const sheetInfo = await dynamicSheetManager.getOrCreateSheet(tableName);
          const tableData = await dynamicSheetManager.getTableDataFromSheet(tableName);
          const isConfigured = tableData && tableData.keyToRowIndex.size > 0;

          if (!isConfigured) {
            unconfigured.add(tableName);
          }

          setSheetInfoMap(prev => {
            const newMap = new Map(prev);
            newMap.set(tableName, sheetInfo);
            return newMap;
          });
        } catch (error: any) {
          console.error(`Failed to check configuration for ${tableName}:`, error);
          // Track the error
          if (error?.message?.includes('No access') || error?.message?.includes('permission')) {
            accessErrors.set(tableName, error.message);
          } else if (error?.message?.includes('No spreadsheet configured')) {
            accessErrors.set(tableName, 'No spreadsheet configured');
          } else {
            accessErrors.set(tableName, 'Error accessing spreadsheet');
          }
        }
      }

      setUnconfiguredTables(unconfigured);
      setAccessErrorTables(accessErrors);
    };

    // Clear sheet info when table names change
    setSheetInfoMap(new Map());
    setIsLoadingSheetInfo(false);
    setUnconfiguredTables(new Set());

    // Check configuration for all tables
    checkAllTablesConfiguration();
  }, [tableNames.join(',')]); // Use string comparison to prevent reference issues

  // Function to load sheet info on-demand (when user clicks Google Sheets link)
  const loadSheetInfoForTable = useCallback(async (tableName: string) => {
    if (sheetInfoMap.has(tableName)) {
      return; // Already loaded
    }

    setIsLoadingSheetInfo(true);

    try {
      // Only get info for the specific table, not all tables
      const sheetInfo = await dynamicSheetManager.getOrCreateSheet(tableName);

      // Check if the sheet is properly configured by checking if it has any data
      const tableData = await dynamicSheetManager.getTableDataFromSheet(tableName);
      const isConfigured = tableData && tableData.keyToRowIndex.size > 0;

      if (!isConfigured) {
        setUnconfiguredTables(prev => new Set([...prev, tableName]));
      } else {
        setUnconfiguredTables(prev => {
          const newSet = new Set(prev);
          newSet.delete(tableName);
          return newSet;
        });
      }

      setSheetInfoMap(prev => {
        const newMap = new Map(prev);
        newMap.set(tableName, sheetInfo);
        return newMap;
      });
    } catch (error) {
      console.error(`Error loading sheet info for ${tableName}:`, error);
    } finally {
      setIsLoadingSheetInfo(false);
    }
  }, [sheetInfoMap]);

  const handleTableToggle = (tableName: string) => {
    const newSelectedTables = new Set(modalState.selectedTables);

    if (newSelectedTables.has(tableName)) {
      newSelectedTables.delete(tableName);
    } else {
      newSelectedTables.add(tableName);
    }

    onStateChange({ selectedTables: newSelectedTables });
  };

  const handleShowAll = () => {
    const newSelectedTables = new Set(tableNames);
    onStateChange({
      selectedTables: newSelectedTables,
      showAllTables: true
    });
  };

  const handleHideAll = () => {
    onStateChange({
      selectedTables: new Set(),
      showAllTables: false
    });
  };

  const handleOpenGoogleSheets = async (tableName: string) => {
    // Load sheet info on-demand when user clicks the link
    if (!sheetInfoMap.has(tableName)) {
      await loadSheetInfoForTable(tableName);
    }

    const masterSpreadsheetId = (GOOGLE_CONFIG as any).masterSpreadsheet;
    const tableSheets = (GOOGLE_CONFIG as any).tableSheets;

    if (!masterSpreadsheetId || !tableSheets?.[tableName]) {
      console.error(`No configuration found for table: ${tableName}`);
      return;
    }

    const gid = tableSheets[tableName].sheetId;
    console.log(`Opening Google Sheet for table: ${tableName} in master spreadsheet (gid=${gid})`);

    const url = `https://docs.google.com/spreadsheets/d/${masterSpreadsheetId}/edit#gid=${gid}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Generate display name from table name (add spacing, capitalize)
  const getDisplayName = (tableName: string): string => {
    return tableName
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
      .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (tableNames.length === 0) {
    return null;
  }

  return (
    <div className="w-72 bg-white border-r border-unity-gray flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-unity-gray bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">String Tables</h3>

        {/* Quick Toggle Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleShowAll}
            disabled={allTablesSelected}
            className={clsx(
              "flex-1 px-3 py-1.5 text-xs rounded transition-colors",
              allTablesSelected
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-unity-blue text-white hover:bg-unity-blue-dark"
            )}
          >
            Show All
          </button>
          <button
            onClick={handleHideAll}
            disabled={noTablesSelected}
            className={clsx(
              "flex-1 px-3 py-1.5 text-xs rounded transition-colors",
              noTablesSelected
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-red-500 text-white hover:bg-red-600"
            )}
          >
            Hide All
          </button>
        </div>
      </div>

      {/* Filter State Indicator */}
      {modalState.selectedTables.size > 0 && modalState.selectedTables.size < tableNames.length && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
            <span className="text-xs text-yellow-700">
              Filters active ({modalState.selectedTables.size} of {tableNames.length} tables)
            </span>
          </div>
        </div>
      )}

      {/* Table List */}
      <div className="flex-1 overflow-y-auto">
        {tableNames.map(tableName => {
          const count = tableCounts[tableName] || 0;
          const isSelected = modalState.selectedTables.has(tableName);
          const displayName = getDisplayName(tableName);
          const sheetInfo = sheetInfoMap.get(tableName);
          const hasSheetInfo = !!sheetInfo;

          return (
            <div key={tableName} className="border-b border-gray-100 last:border-b-0">
              <div className="flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors">
                {/* Checkbox */}
                <label className="flex items-start gap-3 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleTableToggle(tableName)}
                    className="mt-1 h-4 w-4 text-unity-blue border-gray-300 rounded focus:ring-unity-blue focus:ring-2"
                  />

                  {/* Table Info */}
                  <div className="flex-1 min-w-0">
                    {/* Display Name */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {displayName}
                      </span>
                      {/* Warning for unconfigured tables */}
                      {unconfiguredTables.has(tableName) && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-md" title="Sheet not properly configured - missing key entries">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs font-medium">Not configured</span>
                        </div>
                      )}
                      {/* Error for inaccessible spreadsheets */}
                      {accessErrorTables.has(tableName) && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-md" title={accessErrorTables.get(tableName)}>
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs font-medium">No access</span>
                        </div>
                      )}
                    </div>

                    {/* Entry Count */}
                    <div className="text-xs text-gray-500 mb-1">
                      {count} {count === 1 ? 'entry' : 'entries'}
                    </div>

                    {/* Table Name (for debugging) */}
                    <div className="text-xs text-gray-400 mt-1 font-mono">
                      Table: {tableName}
                    </div>
                  </div>
                </label>

                {/* Info Icon with Hover Popover */}
                <div
                  ref={(el) => {
                    if (el) iconRefs.current.set(tableName, el);
                    else iconRefs.current.delete(tableName);
                  }}
                  className="relative mt-1"
                  onMouseEnter={() => {
                    if (hoverTimeoutId !== null) {
                      clearTimeout(hoverTimeoutId);
                      setHoverTimeoutId(null);
                    }
                    setHoveredTable(tableName);
                  }}
                  onMouseLeave={() => {
                    const timeoutId = window.setTimeout(() => {
                      setHoveredTable(null);
                      setHoverTimeoutId(null);
                    }, 200);
                    setHoverTimeoutId(timeoutId);
                  }}
                >
                  <div
                    className={clsx(
                      "p-1.5 rounded-full transition-colors cursor-help",
                      isLoadingSheetInfo
                        ? "text-gray-300"
                        : "text-gray-400 hover:text-unity-blue hover:bg-blue-50"
                    )}
                  >
                    {isLoadingSheetInfo ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>

                  {/* Ultra-thin Popover with Portal */}
                  {hoveredTable === tableName && (() => {
                    const iconElement = iconRefs.current.get(tableName);
                    if (!iconElement) return null;

                    const rect = iconElement.getBoundingClientRect();

                    return createPortal(
                      <div
                        className="fixed w-72 z-[10500]"
                        style={{
                          left: `${rect.right + 8}px`,
                          top: `${rect.top}px`,
                        }}
                        onMouseEnter={() => {
                          if (hoverTimeoutId !== null) {
                            clearTimeout(hoverTimeoutId);
                            setHoverTimeoutId(null);
                          }
                          setHoveredTable(tableName);
                        }}
                        onMouseLeave={() => {
                          const timeoutId = window.setTimeout(() => {
                            setHoveredTable(null);
                            setHoverTimeoutId(null);
                          }, 200);
                          setHoverTimeoutId(timeoutId);
                        }}
                      >
                        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2">
                          {/* Sheet Info */}
                          <div className="text-xs space-y-1">
                            <div className="flex items-start gap-2">
                              <span className="text-gray-500 font-medium whitespace-nowrap">Master Spreadsheet:</span>
                              <span className="text-gray-700 font-mono text-[10px] break-all">
                                {(GOOGLE_CONFIG as any).masterSpreadsheet || 'Not configured'}
                              </span>
                            </div>
                            {(() => {
                              const tableSheets = (GOOGLE_CONFIG as any).tableSheets;
                              const sheetConfig = tableSheets?.[tableName];
                              if (sheetConfig) {
                                return (
                                  <>
                                    <div className="flex items-start gap-2">
                                      <span className="text-gray-500 font-medium whitespace-nowrap">Sheet Name:</span>
                                      <span className="text-gray-700">{sheetConfig.sheetName}</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                      <span className="text-gray-500 font-medium whitespace-nowrap">Sheet ID (gid):</span>
                                      <span className="text-gray-700 font-mono text-xs">{sheetConfig.sheetId}</span>
                                    </div>
                                  </>
                                );
                              }
                              return (
                                <div className="text-gray-500 italic">No sheet configured for this table</div>
                              );
                            })()}
                          </div>

                          {/* Divider */}
                          <div className="border-t border-gray-100 my-2"></div>

                          {/* Open Link */}
                          <button
                            onClick={() => handleOpenGoogleSheets(tableName)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-xs text-unity-blue hover:text-white bg-white hover:bg-unity-blue border border-unity-blue rounded-md transition-colors"
                          >
                            <span>Open in Google Sheets</span>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                        </div>
                      </div>,
                      document.body
                    );
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="px-4 py-3 border-t border-unity-gray bg-gray-50">
        <div className="text-xs text-gray-600">
          <div className="flex justify-between">
            <span>Total Tables:</span>
            <span>{tableNames.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Selected:</span>
            <span>{modalState.selectedTables.size}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export { TableFilterSidebar };
